// System-prompt cascade scopes (epic #635, slice 2) — the owner/maintainer/member
// prose layers that specialize the immutable platform floor. Three optional levels
// cascade `platform → org → repo → user`; each present layer is CONCATENATED in
// that order at compose time (prose layering, not a `prompt_vars` key override).
// The platform floor is always present and lives in code (`platform.md`), never in
// this store.

import { z } from 'zod';
import { AccountIdSchema } from '../value-objects/ids';
import { type TaskEnvelope, TaskOrigin } from '../value-objects/task-envelope';
import { type RawTask, RawTaskSource } from './raw-task';

/**
 * The cascade level a system-prompt layer attaches to. `org` is the whole account
 * (broadest), `repo` one repository, `user` one member's own dispatched work
 * (narrowest). Ordering here is the CONCATENATION order (org → repo → user), so a
 * later layer refines an earlier one.
 */
export const SystemPromptScope = {
  Org: 'org',
  Repo: 'repo',
  User: 'user',
} as const;
export const SystemPromptScopeSchema = z.enum(SystemPromptScope);
export type SystemPromptScope = z.infer<typeof SystemPromptScopeSchema>;

/** Concatenation order of the prose layers (org broadest → user narrowest). */
export const SYSTEM_PROMPT_SCOPE_ORDER: readonly SystemPromptScope[] = [
  SystemPromptScope.Org,
  SystemPromptScope.Repo,
  SystemPromptScope.User,
];

/**
 * The JOB a stored prose layer is written FOR — the second dimension of the
 * cascade. A scope says WHOSE prose it is (account / repo / member); a role says
 * WHICH RUN reads it, so a repo can tell its scout to survey a certain way without
 * that prose reaching every coding run on the same repo.
 *
 * The all-roles sentinel is the EMPTY STRING and it is deliberately NOT a member
 * here — a reader will otherwise try to add one. `''` is the ABSENCE of a role, not
 * a role: it is what the stored column holds for prose that applies to every run,
 * and modelling it as an enum member would make "applies to everything" spellable
 * two ways (`''` and `'all'`) in a UNIQUE key, which is how a row silently stops
 * being found by the resolver that wrote it. `PromptRoleOrAll` below is the type
 * that admits it, at the ONE seam that stores and queries the column.
 *
 * Distinct from `AgentRole` (`@developerz/agent` `prompts/roles.ts`), which names
 * the SERVER-AUTHORED doctrine artifact folded in under the platform floor. These
 * name a slot CUSTOMERS author prose into. The two rosters overlap but are not the
 * same list and must not be merged: adding a doctrine artifact is our decision,
 * adding a customer-authorable slot is a stored-vocabulary change.
 */
export const PromptRole = {
  Architect: 'architect',
  Setup: 'setup',
  Scout: 'scout',
  Worker: 'worker',
  Reviewer: 'reviewer',
  Janitor: 'janitor',
  Triage: 'triage',
  /**
   * Infrastructure work dispatched under `TaskOrigin.Devops`. It has its OWN slot
   * deliberately, rather than sharing {@link PromptRole.Worker}: devops runs reach
   * live infrastructure behind the `ops_action` gate, so an operator's guidance for
   * them is not the same prose as guidance for a coding run. Splitting it later
   * would silently re-point prose an operator had already authored into the worker
   * row, which is exactly the kind of change that cannot be walked back.
   */
  Devops: 'devops',
} as const;
export const PromptRoleSchema = z.enum(PromptRole);
export type PromptRole = z.infer<typeof PromptRoleSchema>;

/** The all-roles sentinel — `system_prompts.role` for prose that applies to every run. */
export const PROMPT_ROLE_ALL = '';

/**
 * What the `system_prompts.role` COLUMN may hold: a role, or the all-roles
 * sentinel. Only the store and its queries speak this type; a caller that has a
 * concrete run in hand speaks {@link PromptRole}.
 */
export type PromptRoleOrAll = PromptRole | typeof PROMPT_ROLE_ALL;

/** Column-level schema for `system_prompts.role` — a role, or `''` for all roles. */
export const PromptRoleColumnSchema = z.union([PromptRoleSchema, z.literal(PROMPT_ROLE_ALL)]);

/**
 * `PromptRole` DOES TWO JOBS, and conflating them is what left the triage door
 * open. One job is SELECTION — which run this is, answered by
 * {@link promptRoleForEnvelope} / {@link promptRoleForRawTask} and handed to the
 * resolver. The other is AUTHORSHIP — which slots a CUSTOMER may write prose into.
 * For seven of the eight members those sets coincide, so one enum served both and
 * nobody noticed there were two questions; then `triage` arrived as a selector
 * value and every write seam that validated against `PromptRoleSchema` inherited it
 * as an authorable slot, with no decision taken anywhere.
 *
 * So the vocabulary stays ONE roster and the two jobs get two names. Removing
 * `Triage` from {@link PromptRoleSchema} was the alternative and it is wrong twice
 * over: `promptRoleForRawTask` must still RETURN it (a raw task's session is a
 * triage run, whoever authored its prose), and this file's own warning above says
 * the roster is stored vocabulary — a value that is in the database and in
 * `role-triage.md` cannot be un-named without the column and the artifact
 * disagreeing with the type.
 *
 * {@link AUTHORABLE_PROMPT_ROLES} is therefore an explicit ALLOWLIST, never a
 * "everything except" derivation: a ninth role added to the roster must be admitted
 * on purpose rather than by default, which is the house whitelist posture. The
 * partition (every role in exactly ONE of the three lists) is asserted in
 * `system-prompt.test.ts` off `PromptRoleSchema.options`, so a role in none of them
 * fails loudly.
 */
export const PLATFORM_ONLY_PROMPT_ROLES = [PromptRole.Triage, PromptRole.Architect] as const;
export type PlatformOnlyPromptRole = (typeof PLATFORM_ONLY_PROMPT_ROLES)[number];

/**
 * Why each platform-only role is ours alone — the sentence a refusal carries at any
 * write seam, so the two transports cannot explain the same rule two ways.
 */
export const PLATFORM_ONLY_PROMPT_ROLE_REASON: Record<PlatformOnlyPromptRole, string> = {
  [PromptRole.Triage]:
    'triage is flight control: it is generic and static, authored only by the platform, so no account or repository can steer how its own asks get judged',
  [PromptRole.Architect]:
    "architect is the plan call's identity, authored only by the platform: no run composes account or repository prose into it, so prose stored here would reach nothing",
};

/**
 * THERE IS NO `UNREACHABLE_PROMPT_ROLES` ANY MORE, and its deletion is arc 103
 * slice 06 rather than a tidy-up.
 *
 * It held exactly one role, `janitor`, refused on a precise and — at the time —
 * true argument: no selector answered it, so a stored layer would compose into
 * nothing, and a `PUT ?role=janitor` answering 200 was "a 200 is not an answer"
 * at a write seam. The file also recorded what would end that: *"the list is a
 * PROMISE ABOUT THE SELECTORS … adding the missing selector therefore MOVES a
 * role out of this list."* {@link promptRoleForJanitorLane} is that selector, the
 * kb-gardener's model call composes the result, and the role moved.
 *
 * The list is DELETED rather than left empty because an empty one takes the
 * refusal arm in {@link promptRoleWriteRefusal} with it into unreachability — a
 * branch that cannot fire, reading like a feature. The distinction it drew is not
 * lost: the partition below is still checked against the selectors in
 * `system-prompt.test.ts`, so a role that ships with no selector fails the
 * partition immediately and forces the same decision, at the moment there is
 * actually something to decide.
 *
 * What did NOT change is the answer to the harder half of the ask this arc
 * serves. A janitor still cannot write the prose it runs on: `JanitorWrite` has
 * no prompt-writing member and no `pr.*` member (the second asserted at compile
 * time), `agent-tuner` still only PROPOSES through `task.file`, and a human or
 * triage still decides. The role became readable; nothing became self-writing.
 */

/**
 * The roles a customer may author prose into. `satisfies` is the compile-time half
 * of the partition: listing a platform-only or unreachable role here does not
 * typecheck.
 *
 * `architect` left this list with the `architect` origin (deleted: no task ever carried
 * it, no `system_prompts` row ever held the role — prod, 2026-09-29): its only selector
 * went with it, and a slot nothing composes is what this list refuses.
 */
export const AUTHORABLE_PROMPT_ROLES = [
  // Authorable since arc 103 slice 06, and only because it became READABLE in the
  // same change: `promptRoleForJanitorLane` answers it and the kb-gardener's model
  // call composes it. Authoring into a slot nothing composes is the shape this
  // partition exists to refuse — see the note on the deleted `UNREACHABLE_PROMPT_ROLES`
  // above.
  PromptRole.Janitor,
  PromptRole.Setup,
  PromptRole.Scout,
  PromptRole.Worker,
  PromptRole.Reviewer,
  PromptRole.Devops,
] as const satisfies readonly Exclude<PromptRole, PlatformOnlyPromptRole>[];
export type AuthorablePromptRole = (typeof AUTHORABLE_PROMPT_ROLES)[number];

/** The role a WRITE seam admits. Every such seam validates against this, never `PromptRoleSchema`. */
export const AuthorablePromptRoleSchema = z.enum(AUTHORABLE_PROMPT_ROLES);

/**
 * The write seam's column-level schema — an authorable role, or the all-roles
 * sentinel. `PromptRoleColumnSchema` stays wide on purpose: it parses a row on the
 * way OUT of the store, and narrowing a READ would turn an already-stored
 * platform-only row into a throw that takes down every compose for that account
 * instead of resolving to nothing.
 */
export const AuthorablePromptRoleColumnSchema = z.union([
  AuthorablePromptRoleSchema,
  z.literal(PROMPT_ROLE_ALL),
]);

/** Whether a value is a slot a customer may author. */
export function isAuthorablePromptRole(value: string): value is AuthorablePromptRole {
  return (AUTHORABLE_PROMPT_ROLES as readonly string[]).includes(value);
}

/** Whether a value names a run whose prose only the platform authors. */
export function isPlatformOnlyPromptRole(value: string): value is PlatformOnlyPromptRole {
  return (PLATFORM_ONLY_PROMPT_ROLES as readonly string[]).includes(value);
}

/**
 * The refusal sentence every write seam shares — the value, the seam, and what to
 * do instead. Here rather than in each transport because two hand-written copies is
 * how one of them ends up naming a role the other refuses.
 *
 * `seam` is the caller's own address (`PUT /v1/system-prompts/org ?role=`, the MCP
 * action) — the transport owns WHERE, this module owns WHY.
 *
 * TWO arms since arc 103 slice 06. It carried a third — "no selector answers this
 * role, so nothing would read what you stored" — for `janitor` alone, and that role
 * is authorable now that a selector answers it and the gardener composes it. The
 * third arm went with the list rather than surviving as a branch nothing can reach.
 * The distinction it protected is still the right one and is written down beside
 * the partition: "we will not let you steer this" and "nothing would ever read
 * this" are different facts, and if a role ever needs the second answer again the
 * partition test fails first and says so.
 */
export function promptRoleWriteRefusal(seam: string, value: string): string {
  const why = isPlatformOnlyPromptRole(value)
    ? `role "${value}" is not authorable — ${PLATFORM_ONLY_PROMPT_ROLE_REASON[value]}`
    : `invalid role "${value}"`;
  return `${seam}: ${why}. Author one of ${AUTHORABLE_PROMPT_ROLES.join(
    ', ',
  )}, or omit the role for prose that applies to every run`;
}

/** Upper bound on a single prose layer — a guard, not a policy. Bounded at write. */
export const SYSTEM_PROMPT_MAX_CHARS = 20_000;

/**
 * One prose layer's text as a WRITE takes it — 1..{@link SYSTEM_PROMPT_MAX_CHARS}
 * chars. Declared here because TWO write edges bound it (`PUT /v1/system-prompts/*`
 * + `/v1/me/system-prompt`, and the MCP `prompt` verb): a surface that re-spelled
 * the bound would admit prose the other refuses, and "clear the layer" is the
 * DELETE / `*_clear` action, never an empty string.
 */
export const SystemPromptTextSchema = z.string().min(1).max(SYSTEM_PROMPT_MAX_CHARS);

/** One `system_prompts` row, parsed on the way OUT of the DB (drift fails loudly). */
export const SystemPromptSchema = z.object({
  id: z.string().min(1),
  accountId: AccountIdSchema,
  scope: SystemPromptScopeSchema,
  /** The instance the prose attaches to (a repo id / user id); `''` for org scope. */
  scopeRef: z.string().max(200),
  /**
   * The run this prose is for, or `''` for every run ({@link PROMPT_ROLE_ALL}).
   * Parsed, not passed through: a drifted value would hold its slot in the unique
   * key while matching no resolve, which is a layer that silently never applies.
   */
  role: PromptRoleColumnSchema,
  text: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type SystemPrompt = z.infer<typeof SystemPromptSchema>;

/**
 * The three optional prose layers resolved for one compose — `undefined`/empty
 * where the account/repo/member authored none. The composer concatenates the
 * present ones in `platform → org → repo → user` order.
 */
export type SystemPromptLayers = {
  org?: string | null;
  repo?: string | null;
  user?: string | null;
};

/** One prose layer as it rides the wire — bounded at the write-boundary guard. */
export const FleetSystemPromptLayerSchema = z.string().max(SYSTEM_PROMPT_MAX_CHARS);

/**
 * The org/repo/user prose layers as they RIDE a `FleetTaskAssignment` (arc 101
 * slice 08, epic #635) — the account/repo/member prompts the mothership resolved
 * (`resolveSystemPromptLayers`, `packages/db`) and snapshots onto the assignment.
 * The box folds each present layer into `composeFleetPrompt` in
 * {@link SYSTEM_PROMPT_SCOPE_ORDER} (`platform → org → repo → user`, narrowest-last),
 * never weakening the immutable platform floor. Each layer is re-bounded at
 * {@link SYSTEM_PROMPT_MAX_CHARS} — the same write-boundary guard, re-asserted at
 * the trust boundary the box parses. An absent layer is an OMITTED key (the wire
 * stays lean; the composer skips a missing layer), never `null` on the wire. Not
 * secret — owner/member-authored prose, same trust class as the box layer.
 */
export const FleetSystemPromptLayersSchema = z.object({
  org: FleetSystemPromptLayerSchema.optional(),
  repo: FleetSystemPromptLayerSchema.optional(),
  user: FleetSystemPromptLayerSchema.optional(),
  // The ROLE-specific prose rides as its OWN key per scope, never concatenated into
  // the generic one beside it. Each layer is bounded at `SYSTEM_PROMPT_MAX_CHARS`
  // and `FleetTaskAssignmentSchema.parse` re-asserts that bound at the producer
  // boundary — so a generic layer folded together with its role layer can exceed it,
  // and the producer then either THROWS at dispatch (the whole assignment lost for a
  // prose overflow) or has to truncate silently, which is worse: the box runs on
  // prose nobody wrote and nothing says so. Separate keys keep every layer
  // independently bounded, and keep it legible ON THE WIRE which stored row a box
  // actually received. Role prose does not REPLACE its generic sibling — both apply,
  // narrowest last (see `composeFleetPrompt`).
  orgRole: FleetSystemPromptLayerSchema.optional(),
  repoRole: FleetSystemPromptLayerSchema.optional(),
  userRole: FleetSystemPromptLayerSchema.optional(),
});
export type FleetSystemPromptLayers = z.infer<typeof FleetSystemPromptLayersSchema>;

/**
 * Which {@link PromptRole}'s stored prose a task envelope's run should read.
 *
 * A SWITCH on `envelope.origin`, exhaustive over `TaskOriginSchema.options`, for the
 * reason {@link runnerRoleCanRunOrigin} (`value-objects/task-envelope.ts`) is one and
 * not a chain of `||`: which role applies is a property of the origin, one origin has
 * exactly one answer, and a chain of disjunctions silently answers the FIRST truthy
 * arm when a later origin is added — here that would hand a new lane the coding
 * repo's prose instead of failing to compile. The `never` arm is the guard: adding a
 * member to `TaskOrigin` breaks the build at this line rather than defaulting.
 * Compare `interactiveRoleFor` (`apps/runner/src/modes/router.ts`), the same shape on
 * the executor side.
 *
 * Total by construction, including for origins that never run an agent: the answer is
 * consumed at resolve time, before anything knows whether the run will execute.
 *
 * Takes only the `origin` (the one field it reads), so a caller holding a stored row's
 * origin and not a whole envelope — the Fleet card's jobs (#6745) — reads the same seam.
 *
 * Pure.
 */
export function promptRoleForEnvelope(envelope: Pick<TaskEnvelope, 'origin'>): PromptRole {
  switch (envelope.origin) {
    case TaskOrigin.Scout:
      return PromptRole.Scout;
    case TaskOrigin.Setup:
      return PromptRole.Setup;
    case TaskOrigin.PrReview:
    case TaskOrigin.PrReviewReply:
      // Both review lanes JUDGE a diff — one on a push, one on a human's reply to a
      // thread we opened — so both read the repo's reviewer prose.
      return PromptRole.Reviewer;
    case TaskOrigin.PrBabysitWatch:
    // The synthetic, born-`completed` watch anchor (#843) never runs a loop, so this
    // arm is unobservable today. It answers with its sibling `pr_babysit` rather than
    // inventing a role, because the day something DOES execute one it will be doing
    // babysit work — and an arm that guesses a fresh role would be a stored-prose
    // slot nobody could have authored for it.
    case TaskOrigin.PrBabysit:
    case TaskOrigin.Conversation:
    case TaskOrigin.GhIssue:
    case TaskOrigin.Sentry:
    case TaskOrigin.Linear:
    case TaskOrigin.Manual:
      // Everything that ends in a diff reads the WORKER prose.
      return PromptRole.Worker;
    case TaskOrigin.Devops:
      // NOT the worker layer. `devops` is its own shipped doctrine role
      // (`AGENT_ROLES`, `@developerz/agent`) and its runs reach live infrastructure
      // behind the `ops_action` gate, so it gets its own authorable slot. It was
      // briefly folded into `worker` here, which would have been a one-way door: an
      // operator authoring devops guidance into the worker row, then later handed a
      // real devops row, silently loses prose they already wrote.
      return PromptRole.Devops;
    case TaskOrigin.TriageJudge:
      // #6759: the prompt arrives COMPOSED from the cluster, so no prose is chosen on the
      // box; the arm answers the caller's role so the table stays total.
      return PromptRole.Triage;
    case TaskOrigin.KbFold:
      // The KB gardener is a janitor pass (`kb-gardener`); same posture as the judge.
      return PromptRole.Janitor;
    case TaskOrigin.PlanAuthor:
      // #7569: the Planner's plan call, composed by the cluster like the judge's; the arm
      // answers the planning role so the table stays total.
      return PromptRole.Architect;
    default: {
      const unreachable: never = envelope.origin;
      throw new Error(`promptRoleForEnvelope: unhandled task origin "${String(unreachable)}"`);
    }
  }
}

/**
 * WHICH POPULATION a janitor pass sweeps — the one fact that decides whether it
 * may be handed tenant prose.
 *
 * Two values, and the split is not a convention: it is the difference between a
 * janitor that holds one account's data for the length of a pass and a janitor
 * that walks every account's at once.
 *
 *   - `account`  — the pass is scoped to ONE account (and usually one repo) and
 *                  acts on its rows, on its BYOK key. `kb-gardener`,
 *                  `project-gardener` and `agent-tuner`.
 *   - `platform` — the pass is cross-tenant by construction: the fleet, the boxes,
 *                  every account's expired artifacts, every account's failed CI.
 *                  `fleet-warden`, `box-warden`, `artifact-reaper`, `bug-scout`.
 *
 * A `platform` lane must never compose a tenant layer, and the fence is
 * structural rather than remembered: such a pass carries no account to resolve
 * one FROM. {@link janitorLaneReadsTenantProse} states it as a value so a caller
 * cannot half-apply it.
 */
export const JANITOR_LANE_SCOPES = ['account', 'platform'] as const;

/** One of {@link JANITOR_LANE_SCOPES}. */
export type JanitorLaneScope = (typeof JANITOR_LANE_SCOPES)[number];

/**
 * THE THIRD SELECTOR, and the reason `janitor` is no longer unreachable.
 *
 * The other two answer "which run is this" from a `TaskEnvelope` or a `RawTask`,
 * and neither can ever answer `Janitor`: a janitor claims no envelope, and a
 * janitor-sourced raw task's session is a TRIAGE run. That was never evidence
 * that a janitor cannot have doctrine — it was evidence that the janitor lane had
 * never been seated on the cascade. It is now: `kb-gardener`'s model call composes
 * `platform -> role(janitor) -> org -> repo` like every other run.
 *
 * Exhaustive with a `never` guard, like its two siblings, so a third lane scope
 * cannot ship without deciding what it composes.
 */
export function promptRoleForJanitorLane(scope: JanitorLaneScope): PromptRole {
  switch (scope) {
    case 'account':
    case 'platform':
      // BOTH answer `Janitor`: the doctrine layer is server-authored and applies
      // to every sweep. What differs is the PROSE below it, which is
      // `janitorLaneReadsTenantProse`'s question, not this one. Folding the two
      // into one function would have made "which doctrine" depend on "whose data",
      // and a cross-tenant sweep with no doctrine at all is not the safer answer.
      return PromptRole.Janitor;
    default: {
      const unreachable: never = scope;
      throw new Error(`promptRoleForJanitorLane: unhandled lane scope "${String(unreachable)}"`);
    }
  }
}

/**
 * May a janitor pass in this lane compose the ACCOUNT's own prose layers?
 *
 * Only an account-scoped one. This is the slice's whole risk stated as a
 * predicate: handing `box-warden` a tenant's prose while it reads every tenant's
 * boxes would let one customer's words steer a sweep over another customer's
 * fleet. The platform floor and the role doctrine are server-authored and reach
 * every lane; nothing below them reaches a cross-tenant pass.
 */
export function janitorLaneReadsTenantProse(scope: JanitorLaneScope): boolean {
  return scope === 'account';
}

/**
 * Which {@link PromptRole}'s stored prose a RAW TASK's triage session reads.
 *
 * {@link promptRoleForEnvelope} cannot answer this and never could: it switches on
 * `TaskEnvelope.origin`, and a raw task is not an envelope — it is the object that
 * exists precisely because nothing has decided yet whether there will BE one
 * (`entities/raw-task.ts`, `docs/idea/lifecycle.md` § "The spine"). So
 * `PromptRole.Triage` and the `role-triage.md` artifact both shipped with no
 * selector that could reach either: the slot was authorable and the doctrine was
 * unreachable.
 *
 * EVERY inlet answers `triage`, and that is the answer rather than an oversight: a
 * raw task is read by exactly one kind of session, the one deciding it, and what
 * varies between inlets is the ask's provenance, not the job being done to it. A
 * per-source role would be a second doctrine for one job, chosen by which door the
 * ask came through.
 *
 * It is still a total `switch` with a `never` guard rather than a constant, for the
 * reason its sibling is one: a TENTH `RawTaskSource` is a stored-vocabulary change,
 * and this is where somebody has to say out loud that the new inlet's asks are
 * still triage's to read. A constant would answer for it silently.
 *
 * Pure.
 */
export function promptRoleForRawTask(rawTask: Pick<RawTask, 'source'>): PromptRole {
  switch (rawTask.source) {
    case RawTaskSource.Scout:
    case RawTaskSource.GhIssue:
    case RawTaskSource.Sentry:
    case RawTaskSource.Linear:
    case RawTaskSource.Webhook:
    case RawTaskSource.Api:
    case RawTaskSource.Janitor:
    case RawTaskSource.BaseRed:
    case RawTaskSource.DeployRed:
    case RawTaskSource.GateBaseRed:
    case RawTaskSource.Devops:
    case RawTaskSource.Conversation:
    case RawTaskSource.Survey:
      return PromptRole.Triage;
    default: {
      const unreachable: never = rawTask.source;
      throw new Error(`promptRoleForRawTask: unhandled raw task source "${String(unreachable)}"`);
    }
  }
}
